import * as d3 from "d3";
import { useEffect, useRef } from "react";
import styled from "styled-components";

export const ColorBox = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  background-color: ${(props) => props.color};
`;

export interface IComparisonData {
  [key: string]: number | null;
}

export interface IArchetypeComparisonChart {
  data: IComparisonData;
  rawData: IComparisonData;
  comparisonData: IComparisonData;
  width: number;
  height: number;
}

export interface SegmentData {
  startAngle: number;
  endAngle: number;
  value: number;
  metric: string;
}

export const archetypeCircleColors = [
  "#00D1A8",
  "#4AF075",
  "#FFC2C2",
  "#FF7800",
  "#64ADDB",
  "#FF3864",
  "#00A1E4",
  "#F2E94E",
  "#8AEAD7",
  "#ACF8C0",
  "#FFE3E3",
  "#FFC18A",
  "#64ADDB",
  "#FFA3B8",
  "#F9F5AE",
  "#D9D9D9",
  "#7C8084",
  "#141414",
];

export const drawCircleSegments = (svg: d3.Selection<SVGGElement, unknown, null, undefined>, segments: SegmentData[], radius: number) => {
  svg.selectAll(".circle-segment").remove();
  svg
    .selectAll(".circle-segment")
    .data(segments)
    .join("path")
    .attr("class", "circle-segment")
    .attr(
      "d",
      d3
        .arc<SegmentData>()
        .innerRadius(radius - 3)
        .outerRadius(radius)
        .startAngle((d) => d.startAngle)
        .endAngle((d) => d.endAngle)
    )
    .style("fill", (_, i) => archetypeCircleColors[i % archetypeCircleColors.length]);
};

export const ArchetypeComparisonChart: React.FC<IArchetypeComparisonChart> = ({ data, rawData, comparisonData, width, height }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!svgRef.current) return;
    d3.select(svgRef.current).selectAll("*").remove();
    const margin = { top: 0, right: 0, bottom: 0, left: 0 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    const radius = Math.min(innerWidth, innerHeight) / 2;
    const validData = Object.entries(data)
      .filter(([_, value]) => value !== null)
      .map(([key, value]) => ({ metric: key, value: value as number }));
    // Create scales
    const angleScale = d3
      .scaleLinear()
      .domain([0, validData.length])
      .range([0, 2 * Math.PI]);
    const radiusScale = d3.scaleLinear().domain([0, 100]).range([0, radius]);
    // Create SVG
    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);
    // Create segments
    const segments = validData.map((d, i) => ({
      startAngle: angleScale(i),
      endAngle: angleScale(i + 1),
      value: d.value,
      metric: d.metric,
      rawScore: rawData[d.metric] || 0,
    }));
    // Draw background circle
    drawCircleSegments(svg, segments, radius);
    // Draw segments
    svg
      .selectAll(".segment")
      .data(segments)
      .join("path")
      .attr("class", "segment")
      .attr(
        "d",
        d3
          .arc<SegmentData>()
          .innerRadius(8)
          .outerRadius((d) => radiusScale(d.value))
          .startAngle((d) => d.startAngle)
          .endAngle((d) => d.endAngle)
      )
      .style("fill", "#00D1A8")
      .style("stroke", "white")
      .style("stroke-width", 1)
      .on("mouseover", function(event, d) {
        d3.select(this).style("fill", "#33DAB9");
        const tooltip = svg.append("g")
          .attr("class", "tooltip-spider")
          .style("pointer-events", "none");
        const angle = (d.startAngle + d.endAngle) / 2;
        const tooltipRadius = radiusScale(d.value) / 2;
        const x = Math.sin(angle) * tooltipRadius;
        const y = -Math.cos(angle) * tooltipRadius;
        const text = tooltip.append("text")
          .attr("text-anchor", "middle")
          .attr("dy", "0.35em")
          .text(d?.rawScore!.toFixed(2))
          .style("font-family", "Aeonik")
          .style("font-size", "13px")
          .style("font-weight", "500")
          .style("fill", "#000000");
        const bbox = text.node()!.getBBox();
        tooltip.insert("rect", "text")
          .attr("x", bbox.x - 8)
          .attr("y", bbox.y - 4)
          .attr("width", bbox.width + 16)
          .attr("height", bbox.height + 8)
          .attr("rx", 2)
          .attr("fill", "white")
          .style("filter", "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))");
        tooltip.attr("transform", `translate(${x},${y})`);
      })
      .on("mouseout", function() {
        d3.select(this).style("fill", "#00D1A8");
        svg.selectAll(".tooltip-spider").remove();
      });
    // Draw segment lines
    svg
      .selectAll(".segment-lines")
      .data(segments)
      .join("path")
      .attr("class", "segment-lines")
      .attr("d", (d) => {
        const startPoint = d3
          .arc<SegmentData>()
          .innerRadius(radiusScale(d.value))
          .outerRadius(radiusScale(d.value))
          .startAngle(d.startAngle)
          .endAngle(d.startAngle)(d);
        const endPoint = d3.arc<SegmentData>().innerRadius(radius).outerRadius(radius).startAngle(d.startAngle).endAngle(d.startAngle)(d);
        return `M ${startPoint!.split("M")[1]} L ${endPoint!.split("M")[1]}`;
      })
      .style("stroke", "#DADADA")
      .style("stroke-width", 1);
    // Draw end lines
    svg
      .selectAll(".segment-end-lines")
      .data(segments)
      .join("path")
      .attr("class", "segment-end-lines")
      .attr("d", (d) => {
        const startPoint = d3
          .arc<SegmentData>()
          .innerRadius(radiusScale(d.value))
          .outerRadius(radiusScale(d.value))
          .startAngle(d.endAngle)
          .endAngle(d.endAngle)(d);
        const endPoint = d3.arc<SegmentData>().innerRadius(radius).outerRadius(radius).startAngle(d.endAngle).endAngle(d.endAngle)(d);
        return `M ${startPoint!.split("M")[1]} L ${endPoint!.split("M")[1]}`;
      })
      .style("stroke", "#DADADA")
      .style("stroke-width", 1);
    if (comparisonData) {
      const comparisonMetrics = validData.map((d) => ({
        ...d,
        comparisonValue: comparisonData[d.metric] || 0,
      }));
      svg
        .selectAll(".comparison-border")
        .data(comparisonMetrics)
        .join("path")
        .attr("class", "comparison-border")
        .attr("d", (d, i) => {
          const startAngle = angleScale(i);
          const endAngle = angleScale(i + 1);
          const currentRadius = radiusScale(d.comparisonValue);
          const nextRadius = radiusScale(comparisonMetrics[(i + 1) % comparisonMetrics.length].comparisonValue);
          // Calculate current arc points
          const x1 = Math.sin(startAngle) * currentRadius;
          const y1 = -Math.cos(startAngle) * currentRadius;
          const x2 = Math.sin(endAngle) * currentRadius;
          const y2 = -Math.cos(endAngle) * currentRadius;
          // Calculate next arc's starting point
          const x3 = Math.sin(endAngle) * nextRadius;
          const y3 = -Math.cos(endAngle) * nextRadius;
          return `M ${x1},${y1} 
                    A ${currentRadius},${currentRadius} 0 0,1 ${x2},${y2}
                    L ${x3},${y3}`;
        })
        .style("fill", "none")
        .style("stroke", "#000000")
        .style("stroke-width", 2);
    }
  }, [data, comparisonData, width, height]);
  return <svg ref={svgRef}></svg>;
};
